// CU-86d45b38j — the saved-card list extracted from PaymentMethodList as dumb presentation; the host owns the data and
// the API contract, so this component never sees a customer id.
import React from "react";
import { Card, List, Button, Space, Modal, Spin, Tag, Typography, Empty } from "antd";
import { CreditCardOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { formatCardDisplay, getCardIcon } from "../../utils/stripe/stripeConfig";

const { Text, Title } = Typography;

export interface PaymentMethodListItem {
	id: string;
	card?: {
		brand: string;
		last4: string;
		exp_month: number;
		exp_year: number;
		funding: string;
	} | null;
	billing_details?: {
		name?: string | null;
	} | null;
}

export interface PaymentMethodListViewProps {
	paymentMethods: PaymentMethodListItem[];
	loading?: boolean;
	actionLoading?: string | null;
	defaultPaymentMethodId?: string | null;
	onAddNew: () => void;
	onSetDefault: (paymentMethodId: string) => void;
	onRemove: (paymentMethodId: string) => void;
}

const PaymentMethodListView: React.FC<PaymentMethodListViewProps> = ({
	paymentMethods,
	loading = false,
	actionLoading = null,
	defaultPaymentMethodId = null,
	onAddNew,
	onSetDefault,
	onRemove,
}) => {
	const confirmRemove = (paymentMethodId: string) => {
		Modal.confirm({
			title: "Remove Payment Method",
			content: "Are you sure you want to remove this payment method?",
			okText: "Remove",
			okType: "danger",
			onOk: () => onRemove(paymentMethodId),
		});
	};

	if (loading) {
		return (
			<Card>
				<div style={{ textAlign: "center", padding: 40 }}>
					<Spin size="large" />
				</div>
			</Card>
		);
	}

	return (
		<Card
			title={
				<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
					<Title level={5} style={{ margin: 0 }}>
						Payment Methods
					</Title>
					<Button type="primary" icon={<PlusOutlined />} onClick={onAddNew} size="small">
						Add New
					</Button>
				</div>
			}
			size="small"
		>
			{paymentMethods.length === 0 ? (
				<Empty description="No payment methods added" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: "40px 0" }}>
					<Button type="primary" icon={<PlusOutlined />} onClick={onAddNew}>
						Add Payment Method
					</Button>
				</Empty>
			) : (
				<List
					dataSource={paymentMethods}
					renderItem={(method) => {
						const card = method.card;
						const isDefault = defaultPaymentMethodId !== null && method.id === defaultPaymentMethodId;

						return (
							<List.Item
								data-testid={`payment-method-${method.id}`}
								actions={[
									<>
										{!isDefault && (
											<Button type="link" size="small" onClick={() => onSetDefault(method.id)} loading={actionLoading === method.id}>
												Set as Default
											</Button>
										)}

										<Button type="link" danger size="small" icon={<DeleteOutlined />} onClick={() => confirmRemove(method.id)} loading={actionLoading === method.id}>
											Remove
										</Button>
									</>,
								].filter(Boolean)}
							>
								<List.Item.Meta
									avatar={<div style={{ fontSize: 32 }}>{card ? getCardIcon(card.brand) : <CreditCardOutlined />}</div>}
									title={
										<Space>
											<Text strong>{card ? formatCardDisplay(card.brand, card.last4) : "Unknown Card"}</Text>
											{isDefault && <Tag color="green">Default</Tag>}
										</Space>
									}
									description={
										<Space direction="vertical" size={0}>
											{card && (
												<>
													<Text type="secondary">
														Expires {card.exp_month.toString().padStart(2, "0")}/{card.exp_year}
													</Text>
													<Text type="secondary" style={{ fontSize: 12 }}>
														{card.funding.toUpperCase()} card
													</Text>
												</>
											)}
											{method.billing_details?.name && (
												<Text type="secondary" style={{ fontSize: 12 }}>
													{method.billing_details.name}
												</Text>
											)}
										</Space>
									}
								/>
							</List.Item>
						);
					}}
				/>
			)}
		</Card>
	);
};

export default PaymentMethodListView;
